import {
  Agent,
  CaptureStatus,
  CheckoutSession,
  getLoanById,
  getOrderDetailsByLoanId,
  LenderName,
  Loan,
  LoanStatusExternal,
  OrderDetails,
  PaymentIntent,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';
import { postCapture } from '@nemuru-eng/js-api-client/slim';
import { v4 as uuidv4 } from 'uuid';

import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { ActionsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Bnpl/NextActions/maps/actions.map';
import { getOrderActions } from '@/components/modules/OrderActivity/hooks/useOrderActions/utils/getOrderActions/getOrderActions';
import { OrderAction } from '@/components/modules/OrderActivity/types/OrderAction';
import { OrderActionType } from '@/components/modules/OrderActivity/types/OrderActionType';
import { LoanStatusExternalMap } from '@/maps/LoanStatusExternal.map';

const MAX_POLLING_ATTEMPTS = 8; // 20 seconds
const POLLING_INTERVAL_MS = 2500; // 2.5 seconds

export class BnplPaymentMethodDetailEntity extends AbstractLoanPaymentMethodDetailEntity {
  static allowed = [PaymentMethodType.BNPL_INSTALMENTS, PaymentMethodType.BNPL_SPLIT_PAY];

  private static readonly DISABLED_ORDER_STATUS_TO_CAPTURE_OR_REFUND = [
    LoanStatusExternal.INITIAL,
    LoanStatusExternal.REQUESTED,
    LoanStatusExternal.APPROVED,
    LoanStatusExternal.ON_HOLD,
    LoanStatusExternal.DENIED,
    LoanStatusExternal.CANCELLED,
    LoanStatusExternal.DESISTED,
    LoanStatusExternal.EXPIRED,
  ];

  private _order: OrderDetails | undefined;

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
  }

  public static isAllowed(type: PaymentMethodType): boolean {
    return this.allowed.includes(type);
  }

  public reset(): this {
    this._order = undefined;
    return super.reset();
  }

  public async syncWithLender(): Promise<void> {
    return;
  }

  public async refresh(): Promise<this> {
    if (!this.paymentMethodId) {
      if (!this.paymentMethod) {
        try {
          this.setPaymentMethodFromCalculator();
        } catch (error) {
          this.setError(error as Error);
        }
      }
      this._isRefreshed = true;
      return this;
    }

    try {
      const loan = await getLoanById({ loanId: this.paymentMethodId });
      const order = await getOrderDetailsByLoanId({ loanId: this.paymentMethodId });
      await this.refreshDataFromLoanAndOrder(loan, order);
      this._isRefreshed = true;
      return this;
    } catch (error) {
      this.setError(error as Error);
      console.error(error);
      return this;
    }
  }

  public async refreshDataFromLoanAndOrder(loan: Loan, order: OrderDetails): Promise<this> {
    await this.setPaymentMethodFromLoan(loan);
    this._loan = loan;
    this._order = order;
    this.hasProviderError = this.getHasProviderError();
    this.actions = (await this.getPendingActions()) ?? undefined;
    this._progress = await this.getProgress();
    return this;
  }

  public async waitUntilSelectedApplication(): Promise<this> {
    const loan = await this.getLoanIfLoanApplicationIsSelected();
    const loanStatusExternal = LoanStatusExternalMap.fromLoanStatus(loan.status);
    this.status = loanStatusExternal;
    return this;
  }

  protected async setPaymentMethodFromLoan(loan: Loan): Promise<void> {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type) as AbstractLoanPaymentMethod;

    if (!(paymentMethodEntity instanceof AbstractLoanPaymentMethod)) {
      throw new Error('Payment method entity is not a loan payment method');
    }

    let lenderNames: LenderName[] | undefined;

    if (loan.loanApplicationSelected) {
      lenderNames = [loan.loanApplicationSelected.lenderName];
    }

    // Costly setup is only performed when needed
    if (!loan.loanApplicationSelected) {
      const totalToFinance = this.totalToFinance();

      paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);

      const candidateLenderNames = paymentMethodEntity.getAllProviderNames();
      lenderNames = candidateLenderNames;

      const processingStrategyLenderNames = this.getLenderNamesFromProcessingStrategy();

      if (processingStrategyLenderNames.length > 0) {
        lenderNames = candidateLenderNames.filter((lenderName) => processingStrategyLenderNames.includes(lenderName));
      }
    }

    paymentMethodEntity.setConditionFromLoanConditions(loan, lenderNames);

    this.paymentMethod = paymentMethodEntity;
  }

  public setPaymentMethodFromCalculator(): void {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type);

    if (!(paymentMethodEntity instanceof AbstractLoanPaymentMethod)) {
      throw new Error('Payment method entity is not a loan payment method');
    }

    const totalToFinance = this.totalToFinance();

    paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);

    this.updateTerm(paymentMethodEntity);

    this.paymentMethod = paymentMethodEntity;
  }

  public isFinalStatus(): boolean {
    if (!this.status) return false;
    if (this.orderCanBeCaptured()) return false;
    if (this.orderCanBeRefunded()) return false;
    return super.isFinalStatus();
  }

  private async getPendingActions(): Promise<Action[] | null> {
    return await ActionsMap.fromPaymentMethodDetail(this);
  }

  public async isActionCompleted(action: Action): Promise<boolean> {
    if (!this._loan) {
      return false;
    }

    if (action === Action.CAPTURE_ORDER) {
      if (!this.status) {
        return false;
      }
      if (this.isCaptured()) {
        return true;
      }
      if (BnplPaymentMethodDetailEntity.DISABLED_ORDER_STATUS_TO_CAPTURE_OR_REFUND.includes(this.status)) {
        return false;
      }
      if (this.orderCanBeCaptured()) {
        return false;
      }
      return true;
    }

    if (action === Action.PAY_FIRST_INSTALMENT) {
      if (this.isCaptured()) {
        return true;
      }
      return this.isConfirmed() || this.orderCanBeCaptured() || this.orderCanBeRefunded();
    }

    if (action === Action.COMPLETE_ONBOARDING) {
      return this.selectedProvider !== undefined && this.lenderRequestId !== undefined;
    }

    if (this.isFinalStatus()) {
      return true;
    }

    return false;
  }

  public getOrderActions(): OrderAction[] | undefined {
    if (!this._order) {
      return;
    }
    const actions = getOrderActions(this._order);

    return actions
      .filter((action) => action.type !== OrderActionType.order)
      .map((action) => {
        if (action.type === OrderActionType.refund) {
          return {
            ...action,
            amount: {
              ...action.amount,
              value: action.amount.value > 0 ? -1 * action.amount.value : action.amount.value,
            },
          };
        }
        return action;
      });
  }

  public isPartiallyCaptured(): boolean {
    if (!this._order) {
      return false;
    }
    return this._order.totalCapturedAmount > 0 && this._order.totalCapturedAmount < this._order.totalOrderAmount;
  }

  public isCaptured(): boolean {
    if (!this._order) {
      return false;
    }
    return this._order.totalCapturedAmount === this._order.totalOrderAmount;
  }

  public orderCanBeCaptured(): boolean {
    if (!this.status) {
      return false;
    }
    if (this.status === LoanStatusExternal.CONFIRMED) {
      return true;
    }
    if (BnplPaymentMethodDetailEntity.DISABLED_ORDER_STATUS_TO_CAPTURE_OR_REFUND.includes(this.status)) {
      return false;
    }
    if (!this._order) {
      return false;
    }
    if (this._order.totalCapturedAmount !== this._order.totalOrderAmount) {
      return true;
    }
    return false;
  }

  public orderCanBeRefunded(): boolean {
    if (!this.status) {
      return false;
    }
    if (BnplPaymentMethodDetailEntity.DISABLED_ORDER_STATUS_TO_CAPTURE_OR_REFUND.includes(this.status)) {
      return false;
    }
    if (!this._order) {
      return false;
    }
    if (this._order.totalCapturedAmount > 0 && this._order.totalRefundedAmount < this._order.totalOrderAmount) {
      return true;
    }
    return false;
  }

  public async captureOrder(): Promise<void> {
    const loanId = this.paymentMethodId;
    if (!loanId) {
      throw new Error('Payment method id is missing');
    }

    if (!this._order) {
      throw new Error('Order is missing');
    }

    const captureId = uuidv4();
    const orderId = this._order.order.id;
    const orderLineIds = this._order.order.items.map((item) => item.id);

    await postCapture({ id: captureId, orderId, orderLineIds });
    const order = await this.getOrderDetailsIfCaptureIsApproved(captureId);
    const loan = await getLoanById({ loanId });
    //const loanStatusExternal = LoanStatusExternalMap.fromLoanStatus(loan.status);
    //this.status = loanStatusExternal;
    await this.refreshDataFromLoanAndOrder(loan, order);
  }

  private async getOrderDetailsIfCaptureIsApproved(captureId: string): Promise<OrderDetails> {
    const loanId = this.paymentMethodId;
    if (!loanId) {
      throw new Error('Payment method id is missing');
    }

    let attempts = 0;
    return new Promise((resolve, reject) => {
      (async function waitForCaptureIsApproved() {
        attempts += 1;
        if (attempts > MAX_POLLING_ATTEMPTS) {
          return reject();
        }

        const newOrder = await getOrderDetailsByLoanId({ loanId });
        const newCapture = newOrder.captures.find((capture) => capture.id === captureId);

        if (
          newCapture &&
          newCapture.notified === true &&
          [CaptureStatus.APPROVED, CaptureStatus.REJECTED].includes(newCapture.status)
        ) {
          return resolve(newOrder);
        }

        setTimeout(waitForCaptureIsApproved, POLLING_INTERVAL_MS);
      })();
    });
  }

  // React context triggers re-renders only when the value passed to the context provider changes (i.e., when the reference changes).
  // However, there are some caveats:
  // Mutating a class instance (e.g., calling methods that change its internal state) does not change the reference.
  // If you do something like entity.refresh() and then call setContextValue(entity), React sees the same object reference and does not re-render consumers.
  // Class instances are "weird" for React because React can't track their internal state changes. React only tracks reference changes for context and props.
  // When you want to update the context with a new state, create a new instance of the class (or a new object) and set that in the context. For example:
  public clone(): this {
    const clone = new BnplPaymentMethodDetailEntity(this.type, this.checkoutSession, this.agent);

    // Copy all properties from this to clone
    Object.assign(clone, this);

    return clone as this;
  }

  public get order(): OrderDetails | undefined {
    return this._order;
  }
}
